import clsx from 'clsx'
import { OpenNew } from '@/assets/icons'
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from '@/components/popover/Popover'
import { LrrContentStatusTag } from '@/agent-v2/references/LrrContentStatusTag'
import { kLrrContentStatusDefinitions, type DisplayableLrrContentStatus } from '@/agent-v2/references/lrr-content-status'

type LrrContentStatusInfoTagProps = {
  status: DisplayableLrrContentStatus
  /** Opens the reference, exactly as clicking the reference row does. Omit to render no CTA. */
  onViewReference?: () => void
  className?: string
}

/**
 * The drift badge plus its explanation, mirroring CaseStatusInfoTag — including how the CTA
 * is gated. Passing `onViewReference` is what makes the link appear, so the surfaces already
 * showing the document (the split-pane and standalone viewers) omit it and get no link, the
 * same way CaseStatusInfoTag gates "View citation analysis".
 */
export function LrrContentStatusInfoTag({ status, onViewReference, className }: LrrContentStatusInfoTagProps) {
  const { heading, body } = kLrrContentStatusDefinitions[status]

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={clsx('inline-flex self-start cursor-pointer focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 rounded', className)}
        >
          <LrrContentStatusTag status={status} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        width="xl"
        showCloseButton
        reserveCloseButtonSpace={false}
        showArrow
        side="bottom"
        align="center"
        sideOffset={8}
        className="max-w-[calc(100vw-2rem)]"
      >
        <div className="flex flex-col text-brand-neutral-950">
          <div className="flex flex-col gap-1">
            <p className="text-sm font-semibold leading-5 tracking-[-0.15px]">{heading}</p>
            {body.map((paragraph, index) => (
              <p key={index} className="text-xs font-normal leading-4">
                {paragraph}
              </p>
            ))}
          </div>
          {onViewReference && (
            <PopoverClose asChild>
              <button
                type="button"
                onClick={onViewReference}
                className="inline-flex items-center gap-0.5 self-start mt-2 text-xs font-medium text-brand-700 underline hover:text-brand-800 cursor-pointer"
              >
                View reference
                <OpenNew className="w-3.5 h-3.5" />
              </button>
            </PopoverClose>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
